import type { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { TlkModalWindowService } from '@itero/ui-toolkit-angular/modal-window';
import { TOOTH_MODAL_ACTION } from '@modules/rx-intuitive-teeth-diagram/models/tooth-modal';
import { ImplantsViewDataRxApi } from '@modules/teeth-diagram/models/implants-view-data';
import { ImplantsViewService } from '@shared/implants-view/implants-view.service';
import { ImplantsViewAppParameter } from '@shared/implants-view/models/implants-view-app-parameter';
import { isDentureRemovableFullDentureImplantBased, isImplantPlanning } from '@shared/utils/procedure-type-util';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RxIntuitiveImplantsViewModalComponent } from './rx-intuitive-implants-view-modal.component';
import type {
	RxIntuitiveImplantsViewModalData,
	RxIntuitiveImplantsViewModalOpenParams,
	RxIntuitiveImplantsViewModalResult,
	ToothForImplantsViewModal
} from './rx-intuitive-implants-view-modal.model';

@Injectable()
export class RxIntuitiveImplantsViewModalService {
	constructor(
		private readonly modalService: TlkModalWindowService,
		private readonly implantsViewService: ImplantsViewService,
		private readonly shellQuery: ShellQuery
	) {}

	open(openParams: RxIntuitiveImplantsViewModalOpenParams): Observable<RxIntuitiveImplantsViewModalResult> {
		const shouldShowCrownSection =
			!isImplantPlanning(openParams.procedureId) &&
			!isDentureRemovableFullDentureImplantBased(openParams.procedureId, openParams.procedureMapTypeId);

		const primaryTooth = openParams.teeth[0];

		const modalData: RxIntuitiveImplantsViewModalData = {
			teeth: openParams.teeth,
			headerToothTitle: openParams.headerToothTitle,
			implantsViewAppParameters$: this.buildImplantsViewAppParameters$({
				teeth: openParams.teeth,
				shouldValidateForSend$: openParams.shouldValidateForSend$,
				isReadonly$: openParams.isReadOnly$,
				procedureId: openParams.procedureId,
				implantsViewData: primaryTooth.implantsViewData
			}),
			shouldShowCrownSection,
			isBridge: openParams.isBridge,
			isReadOnly$: openParams.isReadOnly$,
			shouldValidateForSend$: openParams.shouldValidateForSend$
		};

		const ref = this.modalService.open<
			RxIntuitiveImplantsViewModalResult,
			RxIntuitiveImplantsViewModalData,
			RxIntuitiveImplantsViewModalComponent
		>(RxIntuitiveImplantsViewModalComponent, {
			panelClass: 'rx-intuitive-implants-view-modal-dialog',
			data: modalData,
			autoFocus: true,
			disableClose: true,
			maxWidth: 'calc(100vw - 32px)',
			minWidth: '640px'
		});

		return ref.closed.pipe(map(result => result ?? { action: TOOTH_MODAL_ACTION.Cancel, payload: { toothPartialUpdate: null } }));
	}

	private buildImplantsViewAppParameters$(params: {
		teeth: readonly ToothForImplantsViewModal[];
		shouldValidateForSend$: Observable<boolean>;
		isReadonly$: Observable<boolean>;
		procedureId: number | undefined;
		implantsViewData: Maybe<ImplantsViewDataRxApi>;
	}): Observable<ImplantsViewAppParameter> {
		const { teeth, shouldValidateForSend$, isReadonly$, procedureId, implantsViewData } = params;

		// attachmentsRxId$ resolves rxId even for new Rx (falls back to preparedNewRxId), unlike rxId$ which stays null until saved
		return combineLatest([shouldValidateForSend$, isReadonly$, this.shellQuery.attachmentsRxId$]).pipe(
			map(([shouldValidateForSend, isReadOnly, rxId]) => ({
				...this.implantsViewService.prepareImplantsViewPreselectedData({
					ToothID: teeth[0].toothId,
					ImplantsViewData: implantsViewData
				}),
				...(rxId && { rxId }),
				shouldValidateForSend,
				isReadOnly,
				isIntuitiveFlowSurgicalPlanning: isImplantPlanning(procedureId)
			}))
		);
	}
}
